import Vue from 'vue'
import VueRouter from 'vue-router'
import History from './history'
import extend from './extend'

import * as C from '@/consts'
import store from '@/store'
import routes from './routes'

Vue.use(VueRouter)

Vue.mixin({
  methods: {
    goTo (location) {
      this.$router.push(location).catch(_e => {})
    },
    replaceTo (location) {
      this.$router.replace(location).catch(_e => {})
    },
    go (n) {
      this.$router.go(n)
    },
    back () {
      this.$router.back()
    },
    forward () {
      this.$router.forward()
    }
  }
})

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return { selector: to.hash }
    } else {
      return { x: 0, y: 0 }
    }
  }
})

/**
 * エラー処理を設定する
 */
router.onError(err => {
  console.error(err)
  store.commit('api/setErrors', [err])
})

/**
 * API アクセストークンを確保する
 *
 * @param {Route} to 次にナビゲーションされる対象のルートオブジェクト
 * @param {Function} next フックを解決するため関数
 * @return {Boolean} フックを解決したら false を返す
 */
async function setApiToken (to, next) {
  // api token を取得する
  const apiToken = store.getters['api/token']
  if (apiToken) {
    return true
  }

  // 新しい api access token を取得する
  const ok = await store.dispatch('api/newToken')
  if (!ok) {
    next({ name: 'not-found' })
    return false
  }

  return true
}

/**
 * 組織コードを store に反映する
 *
 * @param {Route} to 次にナビゲーションされる対象のルートオブジェクト
 * @param {Function} next フックを解決するため関数
 * @return {Boolean} フックを解決したら false を返す
 */
async function setOrg (to, next) {
  const orgcode = to.params.orgcode

  if (!orgcode) {
    return true
  }

  // orgcode パラメータを store に設定する
  const ok = await store.dispatch('org/setOrgCode', orgcode)
  if (!ok) {
    next({ name: 'not-found' })
    return false
  }

  return true
}

/**
 * ログインが必要なページかを判定する
 *
 * @param {Number} type ページタイプ
 */
function isNeedAuth (type) {
  if (!type) {
    return false
  }

  const need = store.getters['org/needAuth'] || C.PAGE_USER
  if (!need) {
    return false
  }
  return need <= type
}

/**
 * ページタイプを解決するガード
 *
 * @param {Route} to 次にナビゲーションされる対象のルートオブジェクト
 * @param {Function} next フックを解決するため関数
 * @return {Boolean} フックを解決したら false を返す
 */
function guardPageType (to, next) {
  if (!to.params.orgcode) {
    return true
  }

  // ログインが必要か?
  const needAuth = isNeedAuth(to.meta.type)
  if (needAuth && !store.getters['user/authed']) {
    // to の path を store に保存する
    store.commit('org/setLoginTo', to)

    // ログインページに移動する
    next({ name: 'login', params: { orgcode: to.params.orgcode } })
    return false
  }

  return true
}

/**
 * 予約ページから離脱したら、予約情報をクリアするガード
 *
 * @param {Route} to 次にナビゲーションされる対象のルートオブジェクト
 * @param {Function} next フックを解決するため関数
 * @return {Boolean} フックを解決したら false を返す
 */
function guardBooking (to, next) {
  if (to.meta.type === C.PAGE_PUBLISHED && store.getters['book/initialized']) {
    store.commit('book/clear')
  }
  return true
}

/**
 * グローバルビフォーガードを設定する
 */
router.beforeEach(async (to, from, next) => {
  try {
    let solved

    // API アクセストークン に関するガード
    solved = await setApiToken(to, next)
    if (!solved) {
      return
    }

    // orgcode に関するガード
    solved = await setOrg(to, next)
    if (!solved) {
      return
    }

    // pagetype に関するガード
    solved = guardPageType(to, next)
    if (!solved) {
      return
    }

    // book に関するガード
    solved = guardBooking(to, next)
    if (!solved) {
      return
    }

    next()
  } catch (err) {
    next(err)
  }
})

// ブラウザ履歴を監視する機能を稼働させる
export const history = new History({ router, store })

// ブラウザ履歴を活用したスマートな push() を提供する
extend(router, store)

export default router
